import {
  Box,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  type GridSize
} from '@mui/material';
import { type ResponsiveStyleValue } from '@mui/system';
import { MagnifyingGlassIcon, SlidersIcon, SortDescendingIcon } from '@phosphor-icons/react';
import { memo } from 'react';

interface SearchSkeletonProps {
  gridSize?: ResponsiveStyleValue<GridSize>;
  cardCount?: number;
}

/**
 * Skeleton card component for loading state
 */
const SkeletonCard = memo(() => (
  <Card
    sx={{
      p: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative'
    }}
  >
    {/* Top right skeleton (for action button) */}
    <Box sx={{ position: 'absolute', top: 12, right: 12 }}>
      <Skeleton variant="circular" width={32} height={32} />
    </Box>

    <Stack spacing={2}>
      {/* Top tags/pills skeleton */}
      <Stack direction="row" spacing={1}>
        <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 12 }} />
        <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 12 }} />
      </Stack>

      {/* Logo/Avatar skeleton with text */}
      <Stack direction="row" spacing={2} alignItems="center">
        <Skeleton variant="circular" width={64} height={64} sx={{ flexShrink: 0 }} />
        <Stack spacing={0.5} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="90%" height={28} />
          <Skeleton variant="text" width="70%" height={20} />
        </Stack>
      </Stack>

      {/* Description lines */}
      <Stack spacing={0.5} sx={{ mt: 2 }}>
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="95%" height={20} />
        <Skeleton variant="text" width="80%" height={20} />
      </Stack>

      {/* Info items skeleton */}
      <Stack spacing={1} sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={120} height={20} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={100} height={20} />
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="circular" width={20} height={20} />
          <Skeleton variant="text" width={140} height={20} />
        </Stack>
      </Stack>

      {/* Bottom button skeleton */}
      <Skeleton variant="rounded" width="100%" height={40} sx={{ mt: 2 }} />
    </Stack>
  </Card>
));

SkeletonCard.displayName = 'SkeletonCard';

/**
 * SearchSkeleton component - displays loading state for search interface
 * Shows disabled search bar, disabled sort/filter buttons, and skeleton cards
 */
export const SearchSkeleton = memo(({
  gridSize = { xs: 12, sm: 6, lg: 4 },
  cardCount = 6
}: SearchSkeletonProps) => {
  return (
    <Stack spacing={2} sx={{ height: '100%', minHeight: 0 }}>
      {/* Disabled Search Bar */}
      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          fullWidth
          placeholder="Search"
          disabled
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyingGlassIcon size={20} />
                </InputAdornment>
              ),
            }
          }}
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            }
          }}
        />
        {/* Disabled Sort Button */}
        <IconButton
          disabled
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            width: 48,
            height: 48,
            color: 'text.disabled',
            '&:hover': { backgroundColor: 'white' }
          }}
        >
          <SortDescendingIcon size={20} />
        </IconButton>
        {/* Disabled Filter Button */}
        <IconButton
          disabled
          sx={{
            backgroundColor: 'white',
            borderRadius: 2,
            width: 48,
            height: 48,
            color: 'text.disabled',
            '&:hover': { backgroundColor: 'white' }
          }}
        >
          <SlidersIcon size={24} />
        </IconButton>
      </Stack>

      {/* Filter tags skeleton */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Skeleton variant="text" width={150} height={24} />
      </Stack>

      {/* Skeleton Cards Grid */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <Grid container spacing={2}>
          {Array.from({ length: cardCount }).map((_, index) => (
            <Grid size={gridSize} key={index}>
              <SkeletonCard />
            </Grid>
          ))}
        </Grid>
      </Box>
    </Stack>
  );
});

SearchSkeleton.displayName = 'SearchSkeleton';

export default SearchSkeleton;

